import { SaveOutlined } from '@ant-design/icons'
import { Col, Input, Row } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GLOBAL_CLIENT } from 'src/helpers/GlobalClient'
import { showMessageError, showMessageSuccess } from 'src/helpers/functions'
import { useStore } from 'src/hooks'

/**
 * Khoi "Link design" + "Link mockup" + "Seller" + "Ma don seller" + "Ten san pham" trong popup chi tiet viec.
 * Moi role deu thay; chi admin/supporter (`canEdit`) sua duoc, role khac chi xem.
 *
 * Hai truong nay khong thuoc luong lam viec cua designer nhu `url`, ma la du lieu
 * quan tri nen tach rieng: sua duoc o moi trang thai (tru viec da xoa) va luu
 * bang API /job/manager-update.
 *
 * `linkDesign` con hien dang hyperlink o cot rieng ngoai bang quan ly viec, con
 * `seller` hien dang text — ca hai deu doc thang tu day.
 */
// Link design do he thong goi API gui sang co the gom nhieu link + ghi chu, ngan cach bang
// dau cach / xuong dong. Giu nguyen toan bo noi dung, chi bien cac doan la link thanh hyperlink.
const LINK_PATTERN = /^(https?:\/\/|www\.)\S+$/i
const renderTextWithLinks = (text: string) =>
   text.split(/(\s+)/).map((part, index) =>
      LINK_PATTERN.test(part) ? (
         <a
            key={index}
            href={/^https?:\/\//i.test(part) ? part : `http://${part}`}
            target="_blank"
            rel="noopener noreferrer external"
         >
            {part}
         </a>
      ) : (
         <React.Fragment key={index}>{part}</React.Fragment>
      )
   )

const FIELDS = ['linkDesign', 'linkMockup', 'seller', 'sellerOrderId', 'productName']

const JobDetailManagerInfo = observer(({ canEdit }: { canEdit: boolean }) => {
   const { t } = useTranslation()
   const JobDetailStore = useStore('JobDetailStore')
   const JobsStore = useStore('JobsStore')
   const { jobDetail } = JobDetailStore
   const [isSaving, setIsSaving] = useState(false)
   // Trang thai 'dang sua do' nam o store de nut Hoan thanh luu luon ma khong can bam Luu
   const isDirty = JobDetailStore.isFieldDirty(...FIELDS)

   const handleChangeLinkDesign = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      JobDetailStore.markFieldDirty('linkDesign')
      JobDetailStore.setJobDetailLinkDesign(event.target.value)
   }

   const handleChangeLinkMockup = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      JobDetailStore.markFieldDirty('linkMockup')
      JobDetailStore.setJobDetailLinkMockup(event.target.value)
   }

   const handleChangeSeller = (event: React.ChangeEvent<HTMLInputElement>) => {
      JobDetailStore.markFieldDirty('seller')
      JobDetailStore.setJobDetailSeller(event.target.value)
   }

   const handleChangeSellerOrderId = (event: React.ChangeEvent<HTMLInputElement>) => {
      JobDetailStore.markFieldDirty('sellerOrderId')
      JobDetailStore.setJobDetailSellerOrderId(event.target.value)
   }

   const handleChangeProductName = (event: React.ChangeEvent<HTMLInputElement>) => {
      JobDetailStore.markFieldDirty('productName')
      JobDetailStore.setJobDetailProductName(event.target.value)
   }

   const handleSave = async () => {
      // Chi bo khoang trang dau/cuoi, giu nguyen dau cach / xuong dong ben trong
      const linkDesign = (jobDetail?.linkDesign || '').trim()
      const linkMockup = (jobDetail?.linkMockup || '').trim()
      const seller = (jobDetail?.seller || '').trim()
      const sellerOrderId = (jobDetail?.sellerOrderId || '').trim()
      const productName = (jobDetail?.productName || '').trim()
      try {
         setIsSaving(true)
         const response = await JobDetailStore.updateJobInfoByManager(jobDetail?.id, {
            linkDesign,
            linkMockup,
            seller,
            sellerOrderId,
            productName
         })
         if (!response) return
         // Va lai dong tuong ung ngoai bang quan ly viec de khong phai tai lai trang
         JobsStore.action_patchJobRow(jobDetail?.id, { linkDesign, linkMockup, seller, sellerOrderId, productName })
         showMessageSuccess('Đã cập nhật')
      } catch (error) {
         showMessageError(error)
      } finally {
         setIsSaving(false)
      }
   }

   const inputStyle = {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '22px',
      color: '#5C5C5C',
      paddingTop: 9,
      paddingBottom: 9,
      border: 'solid 1px #dadada'
   }

   // Role khong co quyen sua: hien dang o chi doc, cung khung voi input
   const readOnlyStyle = {
      paddingLeft: 11,
      paddingRight: 11,
      borderRadius: 6,
      minHeight: 42,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const
   }

   return (
      <>
         <Row className="gx-flex-row gx-align-items-center gx-justify-content-between gx-mx-1" wrap={false}>
            <h4 style={{ fontSize: 14, fontWeight: 600, lineHeight: '22px', color: '#272944' }}>
               {`${t(GLOBAL_CLIENT.linkDesign)} / ${t(GLOBAL_CLIENT.linkMockup)} / ${t(GLOBAL_CLIENT.seller)} / ${t(GLOBAL_CLIENT.sellerOrderId)} / Tên sản phẩm`}
            </h4>
            {canEdit && isDirty && (
               <div className="gx-module-logo gx-mb-2" onClick={isSaving ? undefined : handleSave}>
                  <SaveOutlined style={{ color: '#272944', marginLeft: 8 }} />
                  <div style={{ fontSize: 14, fontWeight: 400, lineHeight: '22px', color: '#272944', marginLeft: 8 }}>
                     {t(GLOBAL_CLIENT.save)}
                  </div>
               </div>
            )}
         </Row>
         {!canEdit ? (
            <Row className="gx-flex-row gx-align-items-center gx-mb-2 gx-mx-1" gutter={8}>
               {/* Link design hien du ca dong, khong cat "...", giu nguyen xuong dong */}
               <Col xs={24} className="gx-mb-2">
                  <div
                     style={{
                        ...inputStyle,
                        paddingLeft: 11,
                        paddingRight: 11,
                        borderRadius: 6,
                        minHeight: 42,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                     }}
                  >
                     {jobDetail?.linkDesign ? renderTextWithLinks(jobDetail.linkDesign) : '-'}
                  </div>
               </Col>
               <Col xs={24} className="gx-mb-2">
                  <div
                     style={{
                        ...inputStyle,
                        paddingLeft: 11,
                        paddingRight: 11,
                        borderRadius: 6,
                        minHeight: 42,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                     }}
                  >
                     {jobDetail?.linkMockup ? renderTextWithLinks(jobDetail.linkMockup) : '-'}
                  </div>
               </Col>
               <Col xs={24} md={12}>
                  <div style={{ ...inputStyle, ...readOnlyStyle }}>{jobDetail?.seller || '-'}</div>
               </Col>
               <Col xs={24} md={12}>
                  <div style={{ ...inputStyle, ...readOnlyStyle }}>{jobDetail?.sellerOrderId || '-'}</div>
               </Col>
               <Col xs={24} className="gx-mt-2">
                  <div style={{ ...inputStyle, ...readOnlyStyle }} title={jobDetail?.productName || ''}>
                     {jobDetail?.productName || '-'}
                  </div>
               </Col>
            </Row>
         ) : (
            <Row className="gx-flex-row gx-align-items-center gx-mb-2 gx-mx-1" gutter={8}>
               {/* Nhieu dong de sua ma khong lam mat xuong dong cua noi dung API gui sang */}
               <Col xs={24} className="gx-mb-2">
                  <Input.TextArea
                     value={jobDetail?.linkDesign || ''}
                     onChange={handleChangeLinkDesign}
                     placeholder={t(GLOBAL_CLIENT.nhaplinkdesign)}
                     allowClear
                     maxLength={1000}
                     autoSize={{ minRows: 1, maxRows: 8 }}
                     style={{ ...inputStyle, paddingTop: 9, paddingBottom: 9 }}
                  />
               </Col>
               <Col xs={24} className="gx-mb-2">
                  <Input.TextArea
                     value={jobDetail?.linkMockup || ''}
                     onChange={handleChangeLinkMockup}
                     placeholder={t(GLOBAL_CLIENT.nhaplinkmockup)}
                     allowClear
                     maxLength={1000}
                     autoSize={{ minRows: 1, maxRows: 8 }}
                     style={{ ...inputStyle, paddingTop: 9, paddingBottom: 9 }}
                  />
               </Col>
               <Col xs={24} md={12}>
                  <Input
                     value={jobDetail?.seller || ''}
                     onChange={handleChangeSeller}
                     placeholder={t(GLOBAL_CLIENT.nhapseller)}
                     allowClear
                     maxLength={255}
                     style={inputStyle}
                  />
               </Col>
               <Col xs={24} md={12}>
                  <Input
                     value={jobDetail?.sellerOrderId || ''}
                     onChange={handleChangeSellerOrderId}
                     placeholder={t(GLOBAL_CLIENT.nhapsellerorderid)}
                     allowClear
                     maxLength={255}
                     style={inputStyle}
                  />
               </Col>
               <Col xs={24} className="gx-mt-2">
                  <Input
                     value={jobDetail?.productName || ''}
                     onChange={handleChangeProductName}
                     placeholder={'Nhập tên sản phẩm'}
                     allowClear
                     maxLength={500}
                     style={inputStyle}
                  />
               </Col>
            </Row>
         )}
      </>
   )
})

export default JobDetailManagerInfo
